import { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { History, TrendingUp, TrendingDown, ChevronDown, Loader2, Share2, Copy, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 25;

function fmtAgo(iso) {
  if (!iso) return "—";
  try {
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch {
    return "—";
  }
}

function fmtStx(micro) {
  const v = (micro || 0) / 1_000_000;
  return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function fmtPrice(v) {
  if (v == null || isNaN(Number(v))) return "—";
  const n = Number(v);
  const decimals = Math.abs(n) < 0.01 ? 8 : Math.abs(n) < 10 ? 4 : 2;
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: decimals, minimumFractionDigits: 2 })}`;
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch {
    return "—";
  }
}

// Short suffix identifying which engine ran the bot, e.g. "/hyper".
function botTypeSuffix(t) {
  const s = String(t || "").toLowerCase();
  if (s.includes("hyper")) return "/hyper";
  if (s.includes("poly")) return "/poly";
  if (s.includes("ai") || s.includes("claude")) return "/ai";
  if (s.includes("arb")) return "/arbitrage";
  return "/arbitrage";
}

/**
 * BotTradeHistory — paginated list of every settled position for a bot.
 * Reads from `/api/prediction-bots/:bot_id/positions/history` which is backed
 * by the append-only `position_history` collection (separate from the bot's
 * in-doc open_positions array which is capped at 30).
 */
export default function BotTradeHistory({ botId, botName, botType, testId = "bot-trade-history" }) {
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [shareItem, setShareItem] = useState(null);
  const [copied, setCopied] = useState(false);

  const buildShareText = (p) => {
    const pct = p.pnl_pct || 0;
    const sign = pct >= 0 ? "+" : "";
    const entry = fmtPrice(p.entry_price_usd ?? p.entry_price);
    const exit = fmtPrice(p.exit_price ?? p.current_price_usd);
    const who = botName ? `${botName}` : "My STXOSHI bot";
    return `${who} just closed a ${(p.side || "").toUpperCase()} trade: ${entry} → ${exit} = ${sign}${pct.toFixed(2)}% 🤖📈`;
  };

  const shareOnX = (p) => {
    const url = "https://stxoshi.com";
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(buildShareText(p))}&url=${encodeURIComponent(url)}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const copyShare = async (p) => {
    try {
      await navigator.clipboard.writeText(`${buildShareText(p)} https://stxoshi.com`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };

  const loadMore = useCallback(
    async (reset = false) => {
      if (loading) return;
      setLoading(true);
      try {
        const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
        if (!reset && cursor) params.set("before", cursor);
        const res = await fetch(
          `${API}/api/prediction-bots/${botId}/positions/history?${params}`
        );
        const data = await res.json();
        if (data?.success) {
          const newItems = data.items || [];
          setItems((curr) => (reset ? newItems : [...curr, ...newItems]));
          setCursor(data.next_cursor);
          if (!data.next_cursor || newItems.length < PAGE_SIZE) setDone(true);
        }
      } catch {
        // silent fail; user can retry
      } finally {
        setLoading(false);
      }
    },
    [botId, cursor, loading]
  );

  useEffect(() => {
    setItems([]);
    setCursor(null);
    setDone(false);
    loadMore(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [botId]);

  return (
    <Card className="glass-card border-border" data-testid={testId}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <History className="w-4 h-4 text-secondary" />
          Trade History
          <Badge variant="outline" className="text-xs ml-auto">
            {items.length}
            {!done && "+"}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {items.length === 0 && !loading ? (
          <p className="text-sm text-muted-foreground text-center py-6">
            No settled trades yet. They'll appear here as the bot trades.
          </p>
        ) : (
          <div className="space-y-1.5">
            {items.map((p, i) => {
              const pnlPct = p.pnl_pct || 0;
              const pnlStx = p.pnl_stxoshi || 0;
              const positive = pnlStx > 0;
              return (
                <div
                  key={`${p.position_id || i}-${p.closed_at}`}
                  className="flex items-center justify-between gap-2 p-2.5 rounded-lg border border-border/40 bg-muted/20 hover:bg-muted/40 transition-colors"
                  data-testid={`history-row-${i}`}
                >
                  <div className="flex flex-col min-w-0 gap-0.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <Badge
                        variant="outline"
                        className={`text-[10px] uppercase ${
                          p.side === "long"
                            ? "bg-cyan-500/10 text-cyan-300 border-cyan-500/30"
                            : "bg-purple-500/10 text-purple-300 border-purple-500/30"
                        }`}
                      >
                        {p.side || "?"}
                      </Badge>
                      <span className="text-xs text-muted-foreground truncate">
                        {fmtStx(p.amount_stxoshi)} STXOSHI
                      </span>
                      <span className="text-[10px] text-muted-foreground truncate hidden sm:inline">
                        · {fmtAgo(p.closed_at)}
                      </span>
                      {p.reason && p.reason !== "auto_settle" && (
                        <span className="text-[10px] text-orange-300/80 uppercase tracking-wider hidden md:inline">
                          · {p.reason}
                        </span>
                      )}
                    </div>
                    {(p.entry_price_usd != null || p.entry_price != null) && (
                      <span
                        className="text-[10px] text-muted-foreground font-mono"
                        data-testid={`history-row-${i}-prices`}
                      >
                        Entry {fmtPrice(p.entry_price_usd ?? p.entry_price)}
                        {" → "}
                        Exit {fmtPrice(p.exit_price ?? p.current_price_usd)}
                      </span>
                    )}
                    <span
                      className="text-[10px] text-muted-foreground/80 font-mono"
                      data-testid={`history-row-${i}-timestamp`}
                    >
                      {fmtDateTime(p.closed_at || p.opened_at)}
                      <span className="text-primary/80">{botTypeSuffix(botType || p.bot_type)}</span>
                    </span>
                  </div>
                  <div className="text-right shrink-0">
                    <div
                      className={`font-mono text-sm font-semibold inline-flex items-center gap-1 ${
                        positive ? "text-green-400" : "text-red-400"
                      }`}
                    >
                      {positive ? (
                        <TrendingUp className="w-3 h-3" />
                      ) : (
                        <TrendingDown className="w-3 h-3" />
                      )}
                      {positive ? "+" : ""}
                      {pnlPct.toFixed(2)}%
                    </div>
                    <div className="text-[10px] text-muted-foreground font-mono">
                      {positive ? "+" : ""}
                      {fmtStx(pnlStx)} STXOSHI
                    </div>
                    <button
                      onClick={() => { setShareItem(p); setCopied(false); }}
                      className="mt-1 inline-flex items-center gap-1 text-[10px] text-secondary/80 hover:text-secondary transition-colors"
                      data-testid={`history-row-${i}-share`}
                    >
                      <Share2 className="w-3 h-3" /> Share
                    </button>
                  </div>
                </div>
              );
            })}
            {!done && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => loadMore(false)}
                disabled={loading}
                className="w-full mt-3"
                data-testid="history-load-more"
              >
                {loading ? (
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                ) : (
                  <ChevronDown className="w-3 h-3 mr-1" />
                )}
                Load more
              </Button>
            )}
            {done && items.length > 0 && (
              <p className="text-[10px] text-muted-foreground text-center py-2">
                · end of history ·
              </p>
            )}
          </div>
        )}
      </CardContent>

      {shareItem && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => setShareItem(null)}
          data-testid="trade-share-modal"
        >
          <div className="relative w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setShareItem(null)}
              className="absolute -top-3 -right-3 z-10 rounded-full bg-muted p-1.5 text-muted-foreground hover:text-foreground"
              data-testid="trade-share-close"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="rounded-2xl border border-border bg-gradient-to-br from-zinc-900 to-black p-6 shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-heading font-bold text-primary tracking-wide">STXOSHI</span>
                <Badge
                  variant="outline"
                  className={`uppercase text-[10px] ${shareItem.side === "long" ? "text-cyan-300 border-cyan-500/40" : "text-purple-300 border-purple-500/40"}`}
                >
                  {shareItem.side || "?"}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground">{botName || "Trading bot"} · closed trade</p>
              <div className={`mt-2 font-mono text-4xl font-bold ${(shareItem.pnl_pct || 0) >= 0 ? "text-green-400" : "text-red-400"}`}>
                {(shareItem.pnl_pct || 0) >= 0 ? "+" : ""}
                {(shareItem.pnl_pct || 0).toFixed(2)}%
              </div>
              <div className={`font-mono text-sm ${(shareItem.pnl_stxoshi || 0) >= 0 ? "text-green-400" : "text-red-400"}`}>
                {(shareItem.pnl_stxoshi || 0) >= 0 ? "+" : ""}
                {fmtStx(shareItem.pnl_stxoshi)} STXOSHI
              </div>
              <div className="mt-4 flex items-center justify-between rounded-lg bg-muted/30 p-3 text-xs font-mono">
                <div>
                  <div className="text-muted-foreground text-[10px]">Entry</div>
                  {fmtPrice(shareItem.entry_price_usd ?? shareItem.entry_price)}
                </div>
                <TrendingUp className="w-4 h-4 text-muted-foreground" />
                <div className="text-right">
                  <div className="text-muted-foreground text-[10px]">Exit</div>
                  {fmtPrice(shareItem.exit_price ?? shareItem.current_price_usd)}
                </div>
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              <Button size="sm" className="flex-1" onClick={() => shareOnX(shareItem)} data-testid="trade-share-x">
                <Share2 className="w-3 h-3 mr-1" /> Share on X
              </Button>
              <Button size="sm" variant="outline" className="flex-1" onClick={() => copyShare(shareItem)} data-testid="trade-share-copy">
                <Copy className="w-3 h-3 mr-1" /> {copied ? "Copied!" : "Copy"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
